/**
 * Yup Adapter
 *
 * Converts library-agnostic validation schemas to Yup schemas.
 * This is the only file that imports Yup directly.
 *
 * To swap validation libraries, create a new adapter (e.g., zod.adapter.ts)
 * and update the ValidationAdapter export.
 */

import * as Yup from 'yup';
import type {
  FieldSchema,
  ArraySchema,
  ObjectSchema,
  ValidationRule,
  ConditionalRule,
  SchemaType,
} from '../schema.types';

// ============================================================================
// Type Definitions
// ============================================================================

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type YupSchema = Yup.AnySchema;

// ============================================================================
// Schema Conversion
// ============================================================================

/**
 * Convert our ObjectSchema to a Yup ObjectSchema
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function toYupSchema(schema: ObjectSchema): Yup.ObjectSchema<any> {
  const yupFields: Record<string, YupSchema> = {};

  for (const [key, fieldSchema] of Object.entries(schema.fields)) {
    if ('fields' in fieldSchema) {
      // Nested object
      yupFields[key] = toYupSchema(fieldSchema as ObjectSchema);
    } else if ('of' in fieldSchema || fieldSchema.type === 'array') {
      // Array schema
      yupFields[key] = arraySchemaToYup(fieldSchema as ArraySchema);
    } else {
      // Field schema
      yupFields[key] = fieldSchemaToYup(fieldSchema as FieldSchema);
    }
  }

  return Yup.object(yupFields);
}

/**
 * Convert a FieldSchema to appropriate Yup schema
 */
function fieldSchemaToYup(schema: FieldSchema): YupSchema {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let yupSchema: any = createBaseYupSchema(schema.type);

  // Apply all rules
  for (const rule of schema.rules) {
    yupSchema = applyRule(yupSchema, rule, schema.type);
  }

  // Apply nullable/optional
  if (schema.nullable) {
    yupSchema = yupSchema.nullable();
  }
  if (schema.optional) {
    yupSchema = yupSchema.optional();
  }

  return yupSchema;
}

/**
 * Convert an ArraySchema to Yup array schema
 */
function arraySchemaToYup(schema: ArraySchema): YupSchema {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let yupSchema: any = Yup.array();

  // Add item schema if defined
  if (schema.of) {
    const itemSchema = fieldSchemaToYup(schema.of);
    yupSchema = yupSchema.of(itemSchema);
  }

  // Apply rules
  for (const rule of schema.rules) {
    switch (rule.type) {
      case 'required':
        yupSchema = yupSchema.required(rule.message ?? 'Required');
        break;
      case 'minValue':
        yupSchema = yupSchema.min(
          rule.value,
          rule.message ?? `Must have at least ${rule.value} items`,
        );
        break;
      case 'maxValue':
        yupSchema = yupSchema.max(
          rule.value,
          rule.message ?? `Must have at most ${rule.value} items`,
        );
        break;
    }
  }

  if (schema.nullable) {
    yupSchema = yupSchema.nullable();
  }
  if (schema.optional) {
    yupSchema = yupSchema.optional();
  }

  return yupSchema;
}

/**
 * Create base Yup schema for a given type
 */
function createBaseYupSchema(type: SchemaType): YupSchema {
  switch (type) {
    case 'string':
      return Yup.string();
    case 'number':
      return Yup.number();
    case 'boolean':
      return Yup.boolean();
    case 'date':
      return Yup.date() as unknown as YupSchema;
    case 'array':
      return Yup.array();
    case 'object':
      return Yup.object();
    default:
      return Yup.string();
  }
}

/**
 * Apply a validation rule to a Yup schema
 */
function applyRule(
  schema: YupSchema,
  rule: ValidationRule,
  type: SchemaType,
): YupSchema {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const s = schema as any;

  switch (rule.type) {
    case 'required':
      return s.required(rule.message ?? 'Required');

    case 'minLength':
      return s.min(
        rule.value,
        rule.message ?? `Must be at least ${rule.value} characters`,
      );

    case 'maxLength':
      return s.max(
        rule.value,
        rule.message ?? `Must be at most ${rule.value} characters`,
      );

    case 'email':
      return s.email(rule.message ?? 'Invalid email address');

    case 'url':
      return s.url(rule.message ?? 'Invalid URL');

    case 'pattern': {
      const pattern =
        typeof rule.value === 'string' ? new RegExp(rule.value) : rule.value;
      return s.matches(pattern, rule.message ?? 'Invalid format');
    }

    case 'minValue':
      return s.min(
        rule.value,
        rule.message ?? `Must be at least ${rule.value}`,
      );

    case 'maxValue':
      return s.max(rule.value, rule.message ?? `Must be at most ${rule.value}`);

    case 'positive':
      return s.positive(rule.message ?? 'Must be a positive number');

    case 'integer':
      return s.integer(rule.message ?? 'Must be an integer');

    case 'isTrue':
      return s.isTrue(rule.message ?? 'Must be checked');

    case 'custom':
      return s.test(
        rule.name,
        rule.message,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (value: unknown, context: any) =>
          rule.test(value, {
            path: context.path,
            parent: context.parent,
            root: context.options.context ?? {},
          }),
      );

    case 'conditional':
      return applyConditionalRule(schema, rule, type);

    default:
      return schema;
  }
}

/**
 * Apply conditional validation rule
 */
function applyConditionalRule(
  schema: YupSchema,
  rule: ConditionalRule,
  type: SchemaType,
): YupSchema {
  const whenFields = Array.isArray(rule.when) ? rule.when : [rule.when];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const s = schema as any;

  return s.when(whenFields, {
    is: (...values: unknown[]) => {
      if (typeof rule.is === 'function') {
        return rule.is(values);
      }
      // For single field, compare directly
      if (values.length === 1) {
        return values[0] === rule.is;
      }
      // For multiple fields, all must match (if rule.is is array) or first must match
      if (Array.isArray(rule.is)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return values.every((v, i) => v === (rule.is as any[])[i]);
      }
      return values[0] === rule.is;
    },
    then: (currentSchema: YupSchema) => {
      let result = currentSchema;
      for (const thenRule of rule.then) {
        result = applyRule(result, thenRule, type);
      }
      return result;
    },
    otherwise: (currentSchema: YupSchema) => {
      if (!rule.otherwise) return currentSchema;
      let result = currentSchema;
      for (const otherwiseRule of rule.otherwise) {
        result = applyRule(result, otherwiseRule, type);
      }
      return result;
    },
  });
}

// ============================================================================
// Public API
// ============================================================================

/**
 * Validation adapter for converting schemas to library-specific format
 */
export const ValidationAdapter = {
  /**
   * Convert our schema to Yup schema
   */
  toLibrarySchema: toYupSchema,

  /**
   * Get the underlying library for advanced use cases
   */
  getLibrary: () => Yup,
};

// ============================================================================
// Re-exported Schema Types (library-agnostic naming)
// ============================================================================

/**
 * Re-export validation library types for use in other modules.
 * This keeps yup.adapter.ts as the single point of library imports.
 * Types use library-agnostic names (no "Yup" prefix) for external API.
 */
export type StringSchema = Yup.StringSchema<
  string | undefined,
  Yup.AnyObject,
  string | undefined
>;

export type NumberSchema = Yup.NumberSchema<
  number | undefined,
  Yup.AnyObject,
  string | undefined
>;

export type BooleanSchema = Yup.BooleanSchema<
  boolean | undefined,
  Yup.AnyObject,
  string | undefined
>;

export type ObjectSchemaType = Yup.ObjectSchema<
  { [x: string]: unknown },
  Yup.AnyObject,
  { [x: string]: unknown },
  ''
>;

export type ArraySchemaType = Yup.ArraySchema<
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Array<{ [x: string]: any; [x: number]: any }> | undefined,
  Yup.AnyObject,
  string | undefined
>;

export type TestConfig = {
  message: Exclude<Yup.TestConfig['message'], undefined>;
  test: TestFunction;
};

export type TestFunction = Yup.TestFunction<string | undefined, Yup.AnyObject>;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type FormValidationSchema<T = any> = Yup.ObjectSchema<
  Yup.AnyObject,
  T,
  Yup.AnyObject,
  ''
>;

export type DateSchema = Yup.DateSchema<
  Date | undefined,
  Yup.AnyObject,
  Date | undefined
>;

/**
 * Create validation schemas (library-agnostic factory)
 */
export const SchemaFactory = {
  string: () => createBaseYupSchema('string') as unknown as StringSchema,
  number: () => createBaseYupSchema('number') as unknown as NumberSchema,
  boolean: () => createBaseYupSchema('boolean') as unknown as BooleanSchema,
  object: () => createBaseYupSchema('object') as unknown as ObjectSchemaType,
  array: () => createBaseYupSchema('array') as unknown as ArraySchemaType,
  date: () => createBaseYupSchema('date') as unknown as DateSchema,
};
